import { useAuthStore } from '../../stores/auth';
import { Modal, useModalClose } from '@/components/ui/Modal';
import type { ButtonHTMLAttributes } from 'react';
import { resolveProfile } from '../../lib/profileAccess';
import { PurchaseOrderAddresses, PurchaseOrderItems, PurchaseOrderDiscount, type LineItem } from './PurchaseOrderEditorParts';
import { loadVendorOptions } from './vendorOptions';
import { billingFromVendor } from './vendorAddress';
import { applyBuyingDefaults } from './buyingDefaults';
import { defaultLineRate } from './lineRate';
import { emptyItem, linesToSave, mergeVendorLines, toLineItem, visibleLines, type LineView, type PoLinesResponse } from './vendorPoLines';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Save, PackageCheck, X } from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';
import { ProductGridSelector, type ProductGridItem } from '@/components/shared/ProductGridSelector';

interface Vendor { id: string; name: string; gstin?: string | null; billingAddress?: unknown; }

interface GrnItem {
  id: string;
  description: string;
  orderedQty: number;
  receivedQty: number;
}


export function PurchaseOrderEditorPage() {
  const { id } = useParams<{ id: string }>();
  const user = useAuthStore(state => state.user);
  const readOnlyStock = ['P05','P06','P07'].includes(resolveProfile(user) ?? '');
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('draft');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorError, setVendorError] = useState('');

  // GRN modal state
  const [showGrn, setShowGrn] = useState(false);
  const [grnItems, setGrnItems] = useState<GrnItem[]>([]);
  const [grnNotes, setGrnNotes] = useState('');
  const [grnSaving, setGrnSaving] = useState(false);

  const [vendorId, setVendorId] = useState('');
  const [vendorGstin, setVendorGstin] = useState('');
  // SUN-285: a vendor picked by Browse products / buying defaults (not by hand) still gets its billing address.
  const [autoVendorId, setAutoVendorId] = useState('');
  const vendorIdRef = useRef('');
  vendorIdRef.current = vendorId;
  const [deliveryDate, setDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [billingAddress, setBillingAddress] = useState<any>({ line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' });
  const [deliveryAddress, setDeliveryAddress] = useState<any>({ line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' });
  const [pickupAddress, setPickupAddress] = useState<any>({ line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' });
  const [items, setItems] = useState<LineItem[]>([emptyItem()]);
  const [showProducts, setShowProducts] = useState(false);
  const [rateHints, setRateHints] = useState<Record<string, string>>({});
  // SUN-240: lines from the picked vendor's products ("Only short" / "All mapped").
  const [vendorLines, setVendorLines] = useState<PoLinesResponse | null>(null);
  const [lineView, setLineView] = useState<LineView>('short');
  const [linesNote, setLinesNote] = useState('');
  const linesRequest = useRef(0);

  // Document-level vendor discount
  const [docDiscountType, setDocDiscountType] = useState<'amount' | 'percentage'>('amount');
  const [docDiscountValue, setDocDiscountValue] = useState<number>(0);

  const handleProductSelect = async (product: ProductGridItem) => {
    const newItem = {
      description: product.name,
      hsnSac: product.hsnCode || '',
      quantity: 1,
      rate: defaultLineRate({ costPrice: product.costPrice }), // cost price, never the selling price (SUN-285)
      gstRate: product.gstRate ?? 18,
      discount: 0,
      productId: product.id,
    };
    let chosen: LineItem = { ...newItem, unit: product.unit ?? null, source: 'manual' };
    let lineVendor = vendorId;
    try {
      const res = await api.get('/purchase-orders/buying-defaults', { params: { productId: product.id } });
      const applied = applyBuyingDefaults(newItem, res.data?.data ?? null, vendorId);
      chosen = { ...chosen, ...applied.item, rate: defaultLineRate({ lastRate: res.data?.data?.lastRate, costPrice: product.costPrice }) };
      lineVendor = lineVendor || applied.vendorId;
      if (!vendorIdRef.current && applied.vendorId) setAutoVendorId(applied.vendorId);
      // Read current state: the buyer may choose a vendor while this request runs.
      // Spec decision 3: a vendor set here does NOT fill lines from its products — only a pick in the Vendor box does.
      setVendorId(current => current || applied.vendorId);
      if (applied.hint) setRateHints(h => ({ ...h, [product.id]: applied.hint! }));
    } catch { /* Defaults are optional; keep the catalogue price when unavailable. */ }
    if (lineVendor) {
      try {
        const res = await api.get('/vendors/products/units', { params: { vendorId: lineVendor, productIds: product.id } });
        const quoted = res.data?.data?.[product.id];
        if (typeof quoted === 'string' && quoted.trim()) chosen = { ...chosen, unit: quoted };
      } catch { /* Unit lookup is optional; the product's own unit stays. */ }
    }
    // Resolve the empty row against current state so concurrent picks both survive.
    setItems(prev => {
      const firstEmpty = prev.findIndex(item => !item.description);
      return firstEmpty >= 0 ? prev.map((item, i) => i === firstEmpty ? chosen : item) : [...prev, chosen];
    });
  };

  useEffect(() => {
    const vendor = vendors.find(v => v.id === vendorId);
    if (vendor) setVendorGstin(vendor.gstin || '');
  }, [vendorId, vendors]);

  // Only a vendor the system picked here (never a saved PO's vendor on open) fills the billing address.
  useEffect(() => {
    if (!autoVendorId) return;
    const vendor = vendors.find(v => v.id === autoVendorId);
    if (!vendor) return;
    if (vendorIdRef.current === autoVendorId) setBillingAddress(billingFromVendor(vendor));
    setAutoVendorId('');
  }, [autoVendorId, vendors]);

  useEffect(() => {
    const controller = new AbortController();
    loadVendorOptions(controller.signal).then(rows => {if(!controller.signal.aborted)setVendors(rows);})
      .catch(() => {if(!controller.signal.aborted)setVendorError('Could not load suppliers. Reopen the order to try again.');});
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    const controller = new AbortController();
    setLoading(true);
    // Replace (not append) — clear first so stale items from a previous PO never
    // persist if React Router reuses this component across route changes.
    setItems([]);
    api.get(`/purchase-orders/${id}`, { signal: controller.signal }).then(res => {
      const po = res.data.data;
      setVendorId(po.vendorId || po.vendor?.id || '');
      setVendorGstin(po.vendor?.gstin || '');
      setStatus(po.status || 'draft');
      setDeliveryDate(po.deliveryDate ? po.deliveryDate.split('T')[0] : '');
      setNotes(po.notes || '');
      setBillingAddress(po.billingAddress || { line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' });
      setDeliveryAddress(po.deliveryAddress || { line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' });
      setPickupAddress(po.pickupAddress || { line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' });
      if (po.discountType) setDocDiscountType(po.discountType);
      if (po.discountValue != null) setDocDiscountValue(Number(po.discountValue));
      const loadedItems: LineItem[] = (po.items || []).map((item: any) => ({
        description: item.description || '',
        hsnSac: item.hsnSac || '',
        quantity: item.quantity || 1,
        rate: item.rate || 0,
        gstRate: item.gstRate ?? 18,
        discount: Number(item.discount) || 0,
        productId: item.productId || '',
        unit: null,
        source: 'manual',
      }));
      // REPLACE (not append) — assign directly, never spread into prev state
      setItems(loadedItems.length > 0 ? loadedItems : [emptyItem()]);
      // The PO line stores no unit: show the vendor's quoted unit, else the product's own unit.
      const productIds = [...new Set(loadedItems.map(i => i.productId).filter(Boolean))];
      const poVendor = po.vendorId || po.vendor?.id || '';
      if (productIds.length) {
        api.get('/vendors/products/units', { params: { ...(poVendor ? { vendorId: poVendor } : {}), productIds: productIds.join(',') }, signal: controller.signal })
          .then(u => { const units = u.data?.data ?? {}; setItems(prev => prev.map(i => i.productId && typeof units[i.productId] === 'string' ? { ...i, unit: units[i.productId] } : i)); })
          .catch(() => { /* units are display only */ });
      }
    }).catch(err => {
      if (err.code === 'ERR_CANCELED') return; // aborted by cleanup — not an error
      alert('Failed to load purchase order');
      navigate('/purchase-orders');
    }).finally(() => setLoading(false));
    // Cleanup: abort the in-flight request when id changes or component unmounts.
    // Without this, a stale response from PO A could resolve after PO B loads
    // and overwrite B's items — producing the duplicate line-item bug.
    return () => controller.abort();
  }, [id, isEdit]);

  const openGrnModal = async () => {
    try {
      const res = await api.get(`/purchase-orders/${id}`);
      const po = res.data.data;
      setGrnItems((po.items || []).map((item: any) => ({
        id: item.id,
        description: item.description || '',
        orderedQty: Number(item.quantity) || 0,
        receivedQty: Number(item.quantity) || 0,
      })));
      setGrnNotes('');
      setShowGrn(true);
    } catch {
      alert('Failed to load PO items for GRN');
    }
  };

  const handleGrnSubmit = async () => {
    const validItems = grnItems.filter(i => i.receivedQty > 0);
    if (validItems.length === 0) { alert('Enter received qty for at least one item'); return; }
    try {
      setGrnSaving(true);
      await api.post(`/purchase-orders/${id}/grn`, {
        items: validItems.map(i => ({ itemId: i.id, receivedQty: i.receivedQty })),
        notes: grnNotes || undefined,
      });
      setShowGrn(false);
      navigate('/purchase-orders');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create GRN');
    } finally {
      setGrnSaving(false);
    }
  };

  const updateItem = (idx: number, field: keyof LineItem, value: string | number | null) => {
    // touched: a line the buyer typed into stays when another vendor is picked or the view switches.
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value, touched: true } : item));
  };

  const removeItem = (idx: number) => {
    setItems(prev => { const rest = prev.filter((_, i) => i !== idx); return rest.length ? rest : [emptyItem()]; });
  };

  /** Only a pick in the Vendor box fills lines (not buying defaults, not opening a saved PO). */
  const loadVendorLines = async (vid: string) => {
    const req = ++linesRequest.current;
    setLinesNote('');
    if (!vid) { setVendorLines(null); setItems(prev => mergeVendorLines(prev, [])); return; }
    try {
      const res = await api.get(`/vendors/products/po-lines/${vid}`);
      if (req !== linesRequest.current) return;
      const data: PoLinesResponse = res.data?.data;
      if (!Array.isArray(data?.lines)) throw new Error('Unexpected vendor product list');
      setVendorLines(data.mappedCount > 0 ? data : null);
      setLineView(data.defaultView);
      if (!data.mappedCount) setLinesNote('No products are mapped to this vendor yet. Use Browse Products.');
      setItems(prev => mergeVendorLines(prev, visibleLines(data.lines, data.defaultView).map(toLineItem)));
    } catch (err: any) {
      if (req !== linesRequest.current) return;
      setVendorLines(null);
      setItems(prev => mergeVendorLines(prev, []));
      setLinesNote(err?.response?.status === 503 ? 'Vendor product list is not switched on yet.' : "Could not load this vendor's products. Use Browse Products.");
    }
  };

  const changeLineView = (view: LineView) => {
    if (!vendorLines) return;
    setLineView(view);
    setItems(prev => mergeVendorLines(prev, visibleLines(vendorLines.lines, view).map(toLineItem)));
  };

  const totals = useMemo(() => {
    let subtotal = 0;
    let totalGst = 0;
    let totalItemDiscount = 0;

    items.forEach(item => {
      const grossAmount = Number(((item.quantity ?? 0) * item.rate).toFixed(2));
      const discountAmount = Number((grossAmount * (item.discount || 0) / 100).toFixed(2));
      const taxableAmount = Number((grossAmount - discountAmount).toFixed(2));
      const lineGst = Number((taxableAmount * (item.gstRate / 100)).toFixed(2));

      subtotal += taxableAmount;
      totalGst += lineGst;
      totalItemDiscount += discountAmount;
    });

    subtotal = Number(subtotal.toFixed(2));
    totalGst = Number(totalGst.toFixed(2));
    totalItemDiscount = Number(totalItemDiscount.toFixed(2));

    // Document-level vendor discount
    let docDiscountAmt = 0;
    let docDiscountPct = 0;
    if (docDiscountType === 'percentage') {
      docDiscountPct = docDiscountValue;
      docDiscountAmt = Number((subtotal * docDiscountValue / 100).toFixed(2));
    } else {
      docDiscountAmt = Number(docDiscountValue.toFixed(2));
      docDiscountPct = subtotal > 0 ? Number((docDiscountValue / subtotal * 100).toFixed(2)) : 0;
    }
    const afterDocDiscount = Number((subtotal - docDiscountAmt).toFixed(2));

    return { subtotal, totalGst, total: Number((afterDocDiscount + totalGst).toFixed(2)), totalItemDiscount, docDiscountAmt, docDiscountPct, afterDocDiscount };
  }, [items, docDiscountType, docDiscountValue]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        vendorId,
        deliveryDate: deliveryDate || undefined,
        notes: notes || undefined,
        billingAddress: billingAddress,
        deliveryAddress: deliveryAddress,
        pickupAddress: pickupAddress,
        discountType: docDiscountType,
        discountValue: docDiscountValue,
        discountPct: totals.docDiscountPct,
        discountAmount: totals.docDiscountAmt,
        items: linesToSave(items),
      };

      if (isEdit) {
        await api.put(`/purchase-orders/${id}`, payload);
      } else {
        await api.post('/purchase-orders', payload);
      }
      navigate('/purchase-orders');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save purchase order');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-gray-500">Loading purchase order...</span>
      </div>
    );
  }

  const inp = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]';
  const lbl = 'mb-1 block text-sm font-medium text-gray-700';

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <button onClick={() => navigate('/purchase-orders')} className="rounded-lg border border-gray-300 p-2 text-gray-500 hover:bg-gray-50">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit Purchase Order' : 'New Purchase Order'}</h1>
        </div>
        {isEdit && !readOnlyStock && status === 'confirmed' && (
          <button onClick={openGrnModal}
            className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700">
            <PackageCheck size={14} />
            Create GRN
          </button>
        )}
        <button onClick={handleSave} disabled={saving || !vendorId || linesToSave(items).length === 0}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {isEdit ? 'Update' : 'Create'} Purchase Order
        </button>
      </div>

      {vendorError && <p role="alert">{vendorError}</p>}
      {/* Form */}
      <div className="space-y-6">
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Order Details</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className={lbl}>Vendor *</label>
              <select value={vendorId} onChange={e => { const vid = e.target.value; setAutoVendorId(''); setVendorId(vid); const picked = vendors.find(v => v.id === vid); setVendorGstin(picked?.gstin || ''); setBillingAddress(billingFromVendor(picked)); if (!isEdit) void loadVendorLines(vid); }} className={inp}>
                <option value="">Select Vendor</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Delivery Date</label>
              <input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} className={inp} />
            </div>
            <div>
              <label className={lbl}>Notes</label>
              <input type="text" value={notes} onChange={e => setNotes(e.target.value)} className={inp} />
            </div>
            <div>
              <label className={lbl}>Vendor GSTIN</label>
              <input type="text" value={vendorGstin} readOnly placeholder="Auto-filled on vendor selection"
                className={`${inp} bg-gray-50 text-gray-500 cursor-not-allowed`} />
            </div>
          </div>
        </div>

        <PurchaseOrderAddresses {...{billingAddress,pickupAddress,deliveryAddress,setBillingAddress,setPickupAddress,setDeliveryAddress,inp}} />
        <PurchaseOrderItems {...{items,setItems,emptyItem,setShowProducts,updateItem,removeItem,rateHints,totals,linesNote}}
          lineToggle={vendorLines ? { shortCount: vendorLines.shortCount, mappedCount: vendorLines.mappedCount, view: lineView, onView: changeLineView } : null} />
        <PurchaseOrderDiscount {...{docDiscountType,setDocDiscountType,docDiscountValue,setDocDiscountValue,totals}} />
      </div>

      {/* Product Grid Selector */}
      <ProductGridSelector open={showProducts} onClose={() => setShowProducts(false)} onSelect={handleProductSelect} />

      {/* GRN Modal */}
      {showGrn && (
        <Modal open onClose={() => setShowGrn(false)} className="!max-w-xl">
            <div className="sticky top-0 z-10 bg-white rounded-t-xl flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Create Goods Receipt Note</h2>
              <RequestCloseButton className="text-gray-400 hover:text-gray-600"><X size={20} /></RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              <p className="text-sm text-gray-500">Enter the quantity received for each item.</p>
              <div className="space-y-3">
                {grnItems.map((item, idx) => (
                  <div key={item.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="mb-2 text-sm font-medium text-gray-800">{item.description}</p>
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <label className="mb-0.5 block text-xs text-gray-500">Ordered Qty</label>
                        <input type="number" value={item.orderedQty} readOnly
                          className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-400 cursor-not-allowed" />
                      </div>
                      <div className="flex-1">
                        <label className="mb-0.5 block text-xs text-gray-500">Received Qty *</label>
                        <input type="number" min={0} max={item.orderedQty} value={item.receivedQty}
                          onChange={e => setGrnItems(prev => prev.map((g, i) => i === idx ? { ...g, receivedQty: parseFloat(e.target.value) || 0 } : g))}
                          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
                <input type="text" value={grnNotes} onChange={e => setGrnNotes(e.target.value)} placeholder="Optional notes"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <RequestCloseButton className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</RequestCloseButton>
              <button onClick={handleGrnSubmit} disabled={grnSaving}
                className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50">
                {grnSaving && <Loader2 size={14} className="animate-spin" />}
                Confirm Receipt
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
